import _ from 'lodash'
import { useCallback, useEffect, useMemo } from 'react'
import { t } from '@lingui/macro'
import { useLingui } from '@lingui/react'
import { z } from 'zod'

import { Tab, Tabs, useMediaQuery, useTheme } from '@mui/material'
import { useSearchParams } from 'react-router-dom'

import { useQueryParam } from '@/hooks/useQueryParam'

export enum PlanningsTabEnum {
  DASHBOARD = 'dashboard',
  ADMIN = 'admin',
}

function getDefaultPlanningTab(isMobile: boolean) {
  return isMobile ? PlanningsTabEnum.ADMIN : PlanningsTabEnum.DASHBOARD
}

function parseExplicitTab(value: string | null) {
  const parsed = z.nativeEnum(PlanningsTabEnum).safeParse(value)
  return parsed.success ? parsed.data : undefined
}

function validateTab(value: string | null, defaultTab: PlanningsTabEnum) {
  return parseExplicitTab(value) ?? defaultTab
}

export function usePlanningsTab() {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  const [searchParams] = useSearchParams()
  return parseExplicitTab(searchParams.get('tab')) ?? getDefaultPlanningTab(isMobile)
}

export function PlanningsTabs() {
  useLingui()
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  const defaultTab = getDefaultPlanningTab(isMobile)
  const validatePlanningTab = useCallback((value: string | null) => validateTab(value, defaultTab), [defaultTab])
  const [searchParams] = useSearchParams()

  const [, setPlanningId] = useQueryParam('planningId', (val) => val ?? '')
  const [, setTab] = useQueryParam('tab', validatePlanningTab)
  const tab = parseExplicitTab(searchParams.get('tab')) ?? defaultTab

  const tabs = useMemo(
    () => [
      { id: 'dashboard', label: t`Dashboard`, value: PlanningsTabEnum.DASHBOARD },
      { id: 'admin', label: t`Planning Admin`, value: PlanningsTabEnum.ADMIN },
    ],
    []
  )

  useEffect(() => {
    if (tab === PlanningsTabEnum.DASHBOARD) {
      setPlanningId(null)
    }
  }, [setPlanningId, tab])

  return (
    <Tabs
      aria-label="planning tabs"
      value={tab}
      onChange={(_event, newValue) => setTab(newValue)}
      variant={isMobile ? 'fullWidth' : 'scrollable'}
      sx={{
        minHeight: { xs: 40, md: 48 },
        '& .MuiTab-root': {
          minHeight: { xs: 40, md: 48 },
          py: { xs: 0.5, md: 1.5 },
        },
      }}
    >
      {_.map(tabs, (tab) => (
        <Tab key={tab.id} label={tab.label} value={tab.value} />
      ))}
    </Tabs>
  )
}
